import BaseService from './BaseService';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { dataTypes } from './Constants';
import type { DataType } from './Constants';
// import ScreenLogService from './ScreenLogService';

/**
 * Tipos de feature admitidos en la URL.
 *
 * OJO: el contrato de la URL usa 'city' donde el resto de la app usa 'place'.
 * @see UrlParamsService.getDataType
 */
export type UrlFeatureType = 'city' | 'hotel' | 'poi' | 'stage' | 'route';

/**
 * Params soportados en la URL. Ninguno es obligatorio: los que falten son null.
 */
export interface UrlParams {
    type: string | null;
    id: string | null;
    lat: string | null;
    lon: string | null;
    width: string | null;
    height: string | null;
    zoom: string | null;
}

/**
 * Helper for URL query params management.
 */
class UrlParamsService extends BaseService {

    static urlParams: URLSearchParams;

    /**
     * Valid feature types accepted in the URL.
     */
    static validTypes: readonly UrlFeatureType[] = ['city', 'hotel', 'poi', 'stage', 'route'];

    /**
     * Params read from the URL. None of them is mandatory.
     */
    static type: string | null;
    static id: string | null;
    static lat: string | null;
    static lon: string | null;
    static width: string | null;
    static height: string | null;
    static zoom: string | null;

    /**
     * Get URL Params.
     */
    static async getUrlParams(): Promise<URLSearchParams> {
        if (!this.urlParams) {
            this.setParamsFromQueryString(await this.resolveQueryString());
        }
        return this.urlParams;
    }

    /**
     * Resolve the query string that carries the app params.
     *
     * On web it is window.location.search. In the native app the WebView loads
     * from its own base URL (https://com.gronze.maps/, see capacitor.config.json),
     * so window.location.search is empty and the deep link params arrive in the
     * launch URL instead (cold start).
     *
     * @return Query string without the leading '?'.
     */
    static async resolveQueryString(): Promise<string> {
        let queryString = window.location.search.substr(1);
        if (!queryString && Capacitor.isNativePlatform()) {
            const launch = await App.getLaunchUrl();
            if (launch && launch.url) {
                queryString = new URL(launch.url).search.substr(1);
            }
        }
        return queryString;
    }

    /**
     * Parse a query string, store it and read the supported params from it.
     * None of them is mandatory; they may or may not be present.
     *
     * @param queryString Query string without the leading '?'.
     */
    static setParamsFromQueryString(queryString: string): void {
        this.urlParams = new URLSearchParams(queryString);

        this.type = this.urlParams.get('type');
        this.id = this.urlParams.get('id');
        this.lat = this.urlParams.get('lat');
        this.lon = this.urlParams.get('lon');
        this.width = this.urlParams.get('width');
        this.height = this.urlParams.get('height');
        this.zoom = this.urlParams.get('zoom');

        // ScreenLogService.open('UrlParamsService.urlParams:\n' + this.urlParams.toString().split('&').join('\n'));
    }

    /**
     * Initialize service.
     *
     * @see AppService.init
     */
    static async init(): Promise<void> {
        await this.getUrlParams();
    }

    /**
     * Re-read the params from a deep link URL received while the app is already
     * running (Capacitor 'appUrlOpen', warm start). Overwrites the current params.
     *
     * @param url Raw deep link URL from the appUrlOpen event.
     */
    static applyUrl(url: string): void {
        this.setParamsFromQueryString(new URL(url).search.substr(1));
    }

    /**
     * Comprueba si un tipo de la URL es uno de los admitidos.
     *
     * Es un type guard: es lo que permite a getDataType devolver un DataType y
     * no un string cualquiera.
     */
    static isValidType(type: string | null): type is UrlFeatureType {
        return type !== null && this.validTypes.includes(type as UrlFeatureType);
    }

    /**
     * Get all the supported params as an object.
     *
     * Any missing param will be null.
     */
    static getParams(): UrlParams {
        return {
            type: this.type,
            id: this.id,
            lat: this.lat,
            lon: this.lon,
            width: this.width,
            height: this.height,
            zoom: this.zoom,
        };
    }

    /**
     * Serialize the current params as a query string.
     *
     * Only the supported params, only those present. Empty string if none.
     *
     * @return Query string without the leading '?'.
     */
    static toQueryString(): string {
        const params = new URLSearchParams();
        Object.entries(this.getParams()).forEach(([key, value]) => {
            if (value) {
                params.set(key, value);
            }
        });
        return params.toString();
    }

    /**
     * Whether the URL carries a location (both lat & lon).
     */
    static hasLocation(): boolean {
        return !!(this.lat && this.lon);
    }

    /**
     * Whether the URL carries a feature (a valid type & an id).
     */
    static hasFeature(): boolean {
        return !!(this.id && this.isValidType(this.type));
    }

    /**
     * Whether the app was opened from an "Abrir en Gronze Maps" card link.
     *
     * Such a link always carries the feature (type & id) and/or a location,
     * while entering maps.gronze.com directly carries no params at all.
     */
    static isCardLink(): boolean {
        return this.hasFeature() || this.hasLocation();
    }

    /**
     * Gets the internal data type for the URL feature type.
     *
     * The URL contract uses 'city' while the data/rest layer uses 'place';
     * the rest of the types match their internal name.
     */
    static getDataType(): DataType | null {
        if (!this.isValidType(this.type)) {
            return null;
        }
        return this.type === 'city' ? dataTypes.place : this.type;
    }
}

export default UrlParamsService;
